/**
 * Hotel Details Search Context Slice
 *
 * Manages search context from DETAILS API response.
 * Used across hotel details, room selection, and booking review flow.
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

// ============================================
// State Types
// ============================================

/**
 * Raw search context from details API response.
 * Structure matches the backend searchContext field.
 */
export type DetailsSearchContextRaw = Record<string, unknown>;

export interface RoomStayCandidates {
  adultCount: number;
  rooms: number;
  childAges: number[];
}

/**
 * Structured search context derived from API response.
 * Used for internal operations.
 */
export interface HotelSearchContext {
  hotelId: string | null;
  checkIn: string | null; // ISO date string (YYYY-MM-DD)
  checkOut: string | null; // ISO date string (YYYY-MM-DD)
  countryCode: string | null;
  cityCode: string | null;
  locationId: string | null;
  locationType: string | null;
  currency: string;
  roomStayCandidates: RoomStayCandidates;
}

export interface SearchContextState {
  /** Raw search context from details API response */
  readonly detailsSearchContext: DetailsSearchContextRaw | null;
  /** Structured search context for internal operations */
  readonly searchContext: HotelSearchContext;
}

// ============================================
// Initial State
// ============================================

export const initialSearchContextState: SearchContextState = {
  detailsSearchContext: null,
  searchContext: {
    hotelId: null,
    checkIn: null,
    checkOut: null,
    countryCode: null,
    cityCode: null,
    locationId: null,
    locationType: null,
    currency: 'INR',
    roomStayCandidates: {
      adultCount: 2,
      rooms: 1,
      childAges: [],
    },
  },
};

// ============================================
// Slice
// ============================================

const searchContextSlice = createSlice({
  name: 'hotels/searchContext',
  initialState: initialSearchContextState,
  reducers: {
    /**
     * Set raw search context from details API response
     * This is the source of truth for details page
     */
    setDetailsSearchContext: (state, action: PayloadAction<DetailsSearchContextRaw | null>) => {
      state.detailsSearchContext = action.payload;
    },

    /**
     * Set structured search context (when landing on hotel details)
     */
    setHotelSearchContext: (state, action: PayloadAction<Partial<HotelSearchContext>>) => {
      state.searchContext = {
        ...state.searchContext,
        ...action.payload,
      };
    },

    /**
     * Update room stay candidates
     */
    setRoomStayCandidates: (state, action: PayloadAction<RoomStayCandidates>) => {
      state.searchContext.roomStayCandidates = action.payload;
    },

    /**
     * Clear search context (when navigating away from hotel flow)
     */
    clearHotelSearchContext: (state) => {
      state.detailsSearchContext = null;
      state.searchContext = initialSearchContextState.searchContext;
    },

    /**
     * Reset search context state
     */
    resetSearchContextState: () => initialSearchContextState,
  },
});

// ============================================
// Actions
// ============================================

export const {
  setDetailsSearchContext,
  setHotelSearchContext,
  setRoomStayCandidates,
  clearHotelSearchContext,
  resetSearchContextState,
} = searchContextSlice.actions;

// ============================================
// Reducer Export
// ============================================

export const searchContextReducer = searchContextSlice.reducer;
